import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Checkbox, Cluster, FormField, Input, Modal, Stack, Tabs, Textarea } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api, ApiError } from "./api";
import { Flash } from "./Flash";
import { notifySaved } from "./savedToast";

interface Placeholder {
  key: string;
  label: string;
}

interface EmailTemplate {
  template_key: string;
  subject: string;
  html_body: string;
  default_subject: string;
  default_html_body: string;
  placeholders: Placeholder[];
  preview_values: Record<string, string>;
  updated_at: string | null;
  is_custom: boolean;
  recipient_count?: number | null;
  attach_receipt?: boolean;
}

const EMAIL_TABS = [
  { key: "trial_welcome", label: "Welcome" },
  { key: "trial_sequence", label: "Trial sequence" },
  { key: "conversion", label: "Conversion" },
  { key: "purchase", label: "Paid signup" },
  { key: "payment_receipt", label: "Payment receipt" },
  { key: "payment_reminder", label: "Payment reminder" },
  { key: "card_expiring", label: "Card expiring" },
  { key: "cancellation", label: "Cancellation" },
  { key: "outage", label: "Planned outage" },
];

const ATTACH_RECEIPT_TABS = new Set(["conversion", "purchase"]);

const SEQUENCE_BOXES = [
  {
    key: "trial_day_10",
    step: "day_10",
    label: "Trial Mail 01",
    hint: "How is the first stretch going? Checklist: company, contact, lead, opportunity, quote.",
  },
  {
    key: "trial_day_20",
    step: "day_20",
    label: "Trial Mail 02",
    hint: "Touch base on leads converting to opportunities.",
  },
  {
    key: "trial_day_25",
    step: "day_25",
    label: "Trial Mail 03",
    hint: "Reminder to register in Settings → Plan & Billing before the trial ends.",
  },
  {
    key: "trial_day_28",
    step: "day_28",
    label: "Trial Mail 04",
    hint: "Second reminder, plus a short survey on what worked and what did not.",
  },
  {
    key: "trial_day_30",
    step: "day_30",
    label: "Trial Mail 05",
    hint: "Final reminder. In-app signup plus the live marketing-site promos.",
  },
  ...[
    [2, "Add one real company."],
    [4, "Create one live deal as an opportunity."],
    [7, "Offer a 15-minute setup call."],
    [10, "Offer to import their contacts from a CSV."],
    [14, "Half the trial gone; one ten-minute task."],
    [18, "Ask what is getting in the way."],
    [22, "Last call before the trial ends; offer an extension."],
  ].map(([day, hint]) => ({
    key: `trial_dormant_d${day}`,
    step: `dormant_d${day}`,
    label: `Re-engage day ${day}`,
    hint: `Only sent while the org has not started using the app. ${hint}`,
  })),
];

const TEMPLATE_KEYS = [
  "trial_welcome",
  "conversion",
  "purchase",
  "payment_receipt",
  "payment_reminder",
  "card_expiring",
  "cancellation",
  "outage",
  ...SEQUENCE_BOXES.map((item) => item.key),
];

interface NurtureCustomer {
  org_id: string;
  display_name: string;
  contact_email?: string | null;
  license_type?: string | null;
  days_elapsed: number;
  days_remaining: number | null;
  sequence_stopped: boolean;
  engagement?: {
    state: "dormant" | "started" | "active";
    logins: number;
    last_login_at: string | null;
    records: number;
    team_size: number;
  } | null;
  steps: Record<string, string | null>;
}

interface NurtureList {
  steps: { step_key: string; day: number; label: string }[];
  customers: NurtureCustomer[];
}

const TAB_COPY: Record<string, { intro: string; preview: string }> = {
  trial_welcome: {
    intro:
      "Sent automatically to everyone who starts a trial. Thank-you copy, password-setup link, and the trial end date. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales, Pro trial ending 21 October 2026.",
  },
  trial_sequence: {
    intro:
      "Five follow-up letters after the Welcome mail, plus a seven-letter re-engagement track for trials that have not started using the app (days 2, 4, 7, 10, 14, 18, 22; one every 48 hours at most; it stops the moment they add an opportunity). The worker sends the next due step once, then ticks that box. A purchase ticks every remaining box so they never get another trial letter. Uncheck a box to allow a resend.",
    preview: "Sample fill for Acme Sales, day 20 of a Pro trial.",
  },
  conversion: {
    intro:
      "Sent when a trial customer becomes a paying customer. Include {{invoice_html}} for the receipt. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales converting to Pro.",
  },
  purchase: {
    intro:
      "Sent when someone pays at signup (not a trial). Thank-you, welcome, and payment receipt. Include {{invoice_html}}. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales buying Pro.",
  },
  payment_receipt: {
    intro:
      "The PDF attached to the Conversion and Paid signup emails when \"Attach payment receipt\" is checked on either of those tabs. Sent once PayFast confirms the payment, a few minutes after signup. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales buying Pro, 20 seats.",
  },
  payment_reminder: {
    intro:
      "Sent to the org admins (BCC sales@saleslox.com) the day before PayFast processes a subscription payment. Monthly subscriptions are charged on the agreed monthly date; yearly ones the configured number of weeks early. Checked daily at 07:00 UTC and sent once per charge date. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales, monthly CRM Pro payment on 3 November 2026.",
  },
  card_expiring: {
    intro:
      "Sent to the org admin (BCC sales@saleslox.com) when the saved card on file expires on or before the org's next scheduled payment. Checked weekly, Monday 08:00 UTC — sent once per card, re-sent only if the card on file changes. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales, Visa ending 4242 expiring 03/2027.",
  },
  cancellation: {
    intro:
      "Sent to the org admins (BCC sales@saleslox.com) the moment a trial or paid subscription is cancelled. Tells them access continues until the end date, and links to Plan & Billing so they can re-subscribe. Images must be public HTTPS URLs.",
    preview: "Sample fill for Acme Sales, access running until 25 October 2026.",
  },
  outage: {
    intro:
      "Sends this HTML notice to every active user. Set the window, preview the mail, then send. Images must be public HTTPS URLs.",
    preview: "Preview uses the start and end times above, or the sample window.",
  },
};

function fillPreview(template: string, values: Record<string, string>): string {
  return template.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, key: string) => {
    return key in values ? values[key] : match;
  });
}

function utcStamp(local: string, fallback: string): string {
  if (!local) return fallback;
  const moment = new Date(`${local}:00Z`);
  if (Number.isNaN(moment.getTime())) return fallback;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const hh = String(moment.getUTCHours()).padStart(2, "0");
  const mm = String(moment.getUTCMinutes()).padStart(2, "0");
  return `${moment.getUTCDate()} ${months[moment.getUTCMonth()]} ${moment.getUTCFullYear()}, ${hh}:${mm} UTC`;
}

export function MailTemplatesPage() {
  const [tab, setTab] = useState("trial_welcome");
  const [sequenceKey, setSequenceKey] = useState("trial_day_10");
  const [rows, setRows] = useState<Record<string, EmailTemplate | null>>({});
  const [drafts, setDrafts] = useState<Record<string, { subject: string; html: string; attachReceipt: boolean }>>({});
  const [nurture, setNurture] = useState<NurtureList | null>(null);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  const editorKey = tab === "trial_sequence" ? sequenceKey : tab;

  async function load(key: string) {
    const next = await api.get<EmailTemplate>(`/operator/email-templates/${key}`);
    setRows((current) => ({ ...current, [key]: next }));
    setDrafts((current) => ({
      ...current,
      [key]: { subject: next.subject, html: next.html_body, attachReceipt: Boolean(next.attach_receipt) },
    }));
    return next;
  }

  async function loadNurture() {
    const next = await api.get<NurtureList>("/operator/trial-nurture");
    setNurture(next);
    return next;
  }

  useEffect(() => {
    void Promise.all([...TEMPLATE_KEYS.map((key) => load(key)), loadNurture()]).catch((err) => {
      setError(err instanceof Error ? err.message : "Template could not be loaded.");
    });
  }, []);

  const current = rows[editorKey] ?? null;
  const draft = drafts[editorKey] ?? { subject: "", html: "", attachReceipt: false };

  const preview = useMemo(() => {
    if (!current) return { subject: "", html: "" };
    let values = current.preview_values;
    if (tab === "outage") {
      const start = utcStamp(startsAt, current.preview_values.starts_at || "the scheduled start");
      const end = utcStamp(endsAt, current.preview_values.ends_at || "the scheduled end");
      const window = startsAt || endsAt
        ? `from ${start} to ${end}`
        : current.preview_values.window;
      values = { ...current.preview_values, starts_at: start, ends_at: end, window };
    }
    return {
      subject: fillPreview(draft.subject, values),
      html: fillPreview(draft.html, values),
    };
  }, [current, draft, tab, startsAt, endsAt]);

  function setDraft(patch: Partial<{ subject: string; html: string; attachReceipt: boolean }>) {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [editorKey]: { ...draft, ...patch },
    }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const next = await api.put<EmailTemplate>(`/operator/email-templates/${editorKey}`, {
        subject: draft.subject,
        html_body: draft.html,
        ...(ATTACH_RECEIPT_TABS.has(editorKey) ? { attach_receipt: draft.attachReceipt } : {}),
      });
      setRows((currentRows) => ({ ...currentRows, [editorKey]: next }));
      setDrafts((currentDrafts) => ({
        ...currentDrafts,
        [editorKey]: {
          subject: next.subject,
          html: next.html_body,
          attachReceipt: Boolean(next.attach_receipt),
        },
      }));
      notifySaved("Email template saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    setError(null);
    try {
      const next = await api.post<EmailTemplate>(`/operator/email-templates/${editorKey}/reset`, {});
      setRows((currentRows) => ({ ...currentRows, [editorKey]: next }));
      setDrafts((currentDrafts) => ({
        ...currentDrafts,
        [editorKey]: {
          subject: next.subject,
          html: next.html_body,
          attachReceipt: Boolean(next.attach_receipt),
        },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed.");
    } finally {
      setBusy(false);
    }
  }

  async function sendOutage() {
    setBusy(true);
    setError(null);
    setSendResult(null);
    try {
      const result = await api.post<{
        recipients: number;
        sent: number;
        skipped: number;
        failed: number;
      }>("/operator/email-templates/outage/send", {
        subject: draft.subject,
        html_body: draft.html,
        starts_at: startsAt || null,
        ends_at: endsAt || null,
      });
      setSendResult(
        `Sent ${result.sent} of ${result.recipients}. Skipped ${result.skipped}, failed ${result.failed}.`,
      );
      setConfirmSend(false);
      await load("outage");
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Send failed.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleNurture(orgId: string, stepKey: string, sent: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/operator/orgs/${orgId}/trial-nurture`, { step_key: stepKey, sent });
      await loadNurture();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the sent flag.");
    } finally {
      setBusy(false);
    }
  }

  const copy = TAB_COPY[tab];
  const previewTitle =
    tab === "trial_welcome"
      ? "Welcome email preview"
      : tab === "trial_sequence"
        ? `${SEQUENCE_BOXES.find((item) => item.key === sequenceKey)?.label || "Trial"} preview`
        : tab === "conversion"
          ? "Conversion email preview"
          : tab === "purchase"
            ? "Paid signup email preview"
            : tab === "payment_receipt"
              ? "Payment receipt preview"
              : tab === "payment_reminder"
                ? "Payment reminder preview"
              : tab === "card_expiring"
                ? "Card expiring preview"
                : tab === "cancellation"
                  ? "Cancellation email preview"
                  : "Outage email preview";

  return (
    <Stack gap={4}>
      <h2>Email</h2>
      <Tabs items={EMAIL_TABS} activeKey={tab} onChange={setTab} />
      {error ? <Flash tone="error">{error}</Flash> : null}
      {!current && !error ? <p>Loading…</p> : null}
      {current ? (
        <Stack gap={4}>
          <p className="admin-muted">{copy?.intro}</p>
          {tab === "trial_sequence" ? (
            <Stack gap={3}>
              <Cluster gap={2}>
                {SEQUENCE_BOXES.map((box) => (
                  <Button
                    key={box.key}
                    type="button"
                    variant={sequenceKey === box.key ? "primary" : "secondary"}
                    onClick={() => setSequenceKey(box.key)}
                  >
                    {box.label}
                  </Button>
                ))}
              </Cluster>
              <p className="admin-muted">
                {SEQUENCE_BOXES.find((box) => box.key === sequenceKey)?.hint}
              </p>
            </Stack>
          ) : null}
          {tab === "outage" && sendResult ? <p>{sendResult}</p> : null}
          <Card>
            <Stack gap={3}>
              <FormField
                label="Subject"
                tooltip="Email subject line. Placeholders like {{org_name}} fill at send time."
              >
                <Input
                  aria-label={tab === "outage" ? "Outage subject" : "Subject"}
                  value={draft.subject}
                  onChange={(event) => setDraft({ subject: event.target.value })}
                />
              </FormField>
              {tab === "outage" ? (
                <Cluster gap={3}>
                  <FormField
                    label="Starts (UTC)"
                    tooltip="When the outage window begins. Shown in the email and used for preview."
                  >
                    <Input
                      type="datetime-local"
                      aria-label="Outage starts"
                      value={startsAt}
                      onChange={(event) => setStartsAt(event.target.value)}
                    />
                  </FormField>
                  <FormField
                    label="Ends (UTC)"
                    tooltip="When the outage window ends. Shown in the email and used for preview."
                  >
                    <Input
                      type="datetime-local"
                      aria-label="Outage ends"
                      value={endsAt}
                      onChange={(event) => setEndsAt(event.target.value)}
                    />
                  </FormField>
                </Cluster>
              ) : null}
              <PlaceholderHelp items={current.placeholders} />
              <FormField
                label="HTML body"
                tooltip="Full HTML for the email. Use the placeholders listed above; they fill at send time."
              >
                <Textarea
                  aria-label={tab === "outage" ? "Outage HTML body" : "HTML body"}
                  className="admin-mail-html"
                  rows={18}
                  value={draft.html}
                  onChange={(event) => setDraft({ html: event.target.value })}
                  spellCheck={false}
                />
              </FormField>
              {tab === "outage" ? (
                <p className="admin-muted">
                  {current.recipient_count ?? 0} active users will be mailed.
                </p>
              ) : null}
              {ATTACH_RECEIPT_TABS.has(editorKey) ? (
                <Stack gap={1}>
                  <Cluster gap={2} align="center">
                    <Checkbox
                      aria-label="Attach payment receipt"
                      checked={draft.attachReceipt}
                      disabled={busy}
                      onChange={(event) => setDraft({ attachReceipt: event.target.checked })}
                    />
                    <span>Attach payment receipt</span>
                  </Cluster>
                  <p className="admin-muted">
                    Renders the Payment receipt template to PDF and attaches it once PayFast confirms
                    the payment, a few minutes after signup. One setting shared between the
                    Conversion and Paid signup tabs, not two independent switches.
                  </p>
                </Stack>
              ) : null}
              <Cluster gap={2}>
                <Button onClick={() => save()} disabled={busy}>
                  {busy ? "Saving…" : "Save template"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => reset()}
                  disabled={busy || !current.is_custom}
                >
                  Restore default
                </Button>
                {tab === "outage" ? (
                  <Button variant="danger" onClick={() => setConfirmSend(true)} disabled={busy}>
                    Email all users
                  </Button>
                ) : null}
              </Cluster>
            </Stack>
          </Card>
          <PreviewCard
            title={previewTitle}
            hint={copy?.preview || ""}
            subject={preview.subject}
            html={preview.html}
          />
          {tab === "trial_sequence" ? (
            <NurtureTracker
              nurture={nurture}
              busy={busy}
              onToggle={(orgId, stepKey, sent) => void toggleNurture(orgId, stepKey, sent)}
            />
          ) : null}
          {tab === "outage" ? (
            <Modal
              isOpen={confirmSend}
              onClose={() => setConfirmSend(false)}
              title="Email all users"
              footer={
                <>
                  <Button type="button" variant="ghost" onClick={() => setConfirmSend(false)}>
                    Cancel
                  </Button>
                  <Button type="button" variant="danger" disabled={busy} onClick={() => sendOutage()}>
                    {busy ? "Sending…" : `Send to ${current.recipient_count ?? 0} users`}
                  </Button>
                </>
              }
            >
              <p>
                This sends the HTML below to every active Saleslox user. It cannot be unsent.
              </p>
            </Modal>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

function PlaceholderHelp({ items }: { items: Placeholder[] }) {
  return (
    <p className="admin-muted">
      Placeholders become real values at send time:
      {" "}
      {items.map((item, index) => (
        <span key={item.key}>
          {index ? ", " : ""}
          <code>{`{{${item.key}}}`}</code>
        </span>
      ))}
      .
    </p>
  );
}

function PreviewCard({
  title,
  hint,
  subject,
  html,
}: {
  title: string;
  hint: string;
  subject: string;
  html: string;
}) {
  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Preview"
          tip="Rendered subject and HTML with sample values, or the outage window dates above."
        />
        <p className="admin-muted">{hint}</p>
        <p>
          <strong>Subject:</strong> {subject}
        </p>
        <iframe className="admin-mail-preview" title={title} sandbox="" srcDoc={html} />
      </Stack>
    </Card>
  );
}

const TRACKER_STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "day_10", label: "Trial Mail 01" },
  { key: "day_20", label: "Trial Mail 02" },
  { key: "day_25", label: "Trial Mail 03" },
  { key: "day_28", label: "Trial Mail 04" },
  { key: "day_30", label: "Trial Mail 05" },
];

function NurtureTracker({
  nurture,
  busy,
  onToggle,
}: {
  nurture: NurtureList | null;
  busy: boolean;
  onToggle: (orgId: string, stepKey: string, sent: boolean) => void;
}) {
  const [search, setSearch] = useState("");
  const allCustomers = nurture?.customers ?? [];
  const query = search.trim().toLowerCase();
  const customers = query
    ? allCustomers.filter(
        (row) =>
          row.display_name.toLowerCase().includes(query) ||
          (row.contact_email || "").toLowerCase().includes(query),
      )
    : allCustomers;
  return (
    <Card>
      <Stack gap={3}>
        <Cluster gap={3} align="center">
          <TipHeading
            label="Sent tracking"
            tip="Per-trial checkboxes for each nurture step. Uncheck a step to allow that letter again."
          />
          <Input
            type="search"
            placeholder="Search company or email"
            aria-label="Search sent tracking by company"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Cluster>
        <p className="admin-muted">
          Each checkbox is one letter. A purchase checks every box so the sequence stops.
          Uncheck to allow that step again. Open a customer to see who each letter went to and
          the email itself, in their Trial sequence email log.
        </p>
        {allCustomers.length === 0 ? (
          <p className="admin-muted">No current trials or recent signups.</p>
        ) : customers.length === 0 ? (
          <p className="admin-muted">No customers match "{search}".</p>
        ) : (
          <div className="admin-nurture-table">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Day</th>
                  <th>Usage</th>
                  {TRACKER_STEPS.map((step) => (
                    <th key={step.key}>{step.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map((row) => (
                  <tr key={row.org_id}>
                    <td>
                      <strong>
                        <Link to={`/customers/${row.org_id}`}>{row.display_name}</Link>
                      </strong>
                      <div className="admin-muted">{row.contact_email || row.org_id}</div>
                      {row.sequence_stopped ? (
                        <span className="admin-badge is-paid">Paid — sequence stopped</span>
                      ) : (
                        <span className="admin-badge is-trial">{row.license_type || "trial"}</span>
                      )}
                    </td>
                    <td>
                      {row.days_elapsed}
                      {row.days_remaining != null ? ` / ${row.days_remaining} left` : ""}
                    </td>
                    <td>
                      {row.engagement ? (
                        <>
                          <span className={`admin-badge is-${row.engagement.state}`}>
                            {row.engagement.state === "dormant" ? "Needs a nudge" : row.engagement.state}
                          </span>
                          <div className="admin-muted">
                            {row.engagement.logins} sign-in{row.engagement.logins === 1 ? "" : "s"} ·{" "}
                            {row.engagement.records} record{row.engagement.records === 1 ? "" : "s"}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    {TRACKER_STEPS.map((step) => (
                      <td key={step.key}>
                        <Checkbox
                          aria-label={`${row.display_name} ${step.label} sent`}
                          checked={Boolean(row.steps[step.key])}
                          disabled={busy}
                          onChange={(event) => onToggle(row.org_id, step.key, event.target.checked)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Stack>
    </Card>
  );
}
